import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';
import { config } from '@/config';
import { billingService } from '@/services/billingService';
import { Loader2, XCircle } from 'lucide-react';

const CardRegisterPopupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;

    const companyId = searchParams.get('companyId');
    if (!companyId) {
      setError('회사 정보가 없습니다.');
      return;
    }

    initRef.current = true;
    initTossPayment(Number(companyId));
  }, []);

  const initTossPayment = async (companyId: number) => {
    if (!config.tossClientKey) {
      setError('토스페이먼츠 클라이언트 키가 설정되지 않았습니다.');
      return;
    }

    try {
      const { customerKey } = await billingService.getCustomerKey(companyId);
      const tossPayments = await loadTossPayments(config.tossClientKey);

      sessionStorage.setItem('billing_popup_mode', 'true');
      sessionStorage.setItem('billing_pending_context', JSON.stringify({ companyId }));

      const payment = tossPayments.payment({ customerKey });
      await payment.requestBillingAuth({
        method: 'CARD',
        successUrl: `${window.location.origin}/billing/card-callback/success`,
        failUrl: `${window.location.origin}/billing/card-callback/fail`,
        customerEmail: undefined,
        customerName: undefined,
      });
    } catch (err: any) {
      if (err?.code === 'USER_CANCEL') {
        window.opener?.postMessage({ type: 'CARD_REGISTER_CANCEL' }, window.location.origin);
        window.close();
      } else {
        setError('카드 등록 중 오류가 발생했습니다.');
      }
    }
  };

  if (error) {
    return (
      <Container>
        <Card>
          <IconWrapper>
            <XCircle size={48} />
          </IconWrapper>
          <Title>{error}</Title>
          <CloseButton onClick={() => window.close()}>닫기</CloseButton>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <SpinnerWrapper>
          <Loader2 size={48} />
        </SpinnerWrapper>
        <Title>카드 등록을 준비하는 중...</Title>
        <Description>잠시만 기다려주세요.</Description>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f8fafc;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  color: #ef4444;
  margin-bottom: 0.5rem;
`;

const SpinnerWrapper = styled.div`
  color: #059669;
  margin-bottom: 0.5rem;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const Description = styled.p`
  font-size: 0.938rem;
  color: #64748b;
  margin: 0;
`;

const CloseButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  border: none;
  background: #059669;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

export default CardRegisterPopupPage;
